import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageLoading } from '@/components/Loading';
import api from '@/lib/api';
import { Calendar, MapPin, Crown, Ticket, Music, ArrowLeft, Building2, Euro } from 'lucide-react';
import { eventJsonLd, truncate, zoneSlug } from '@/lib/seo';
import { trackEvent } from '@/components/GA4';
import { getEventImage, trackEventBackend } from '@/lib/eventHelpers';

export default function EventoDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/eventi/${slug}`).then((r) => {
      setData(r.data);
      // Track view (deduped per session by helper)
      if (r.data?.evento?.slug) trackEventBackend(r.data.evento.slug, 'view');
    }).catch(() => setError(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (<PublicLayout><PageLoading /></PublicLayout>);
  if (error || !data) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-3xl mb-3">Evento non trovato</h1>
        <p className="text-[color:var(--text-muted)] mb-6">L'evento che cerchi non è disponibile o è stato rimosso.</p>
        <Link to="/"><Button variant="outline" data-testid="event-not-found-back">Torna alla home</Button></Link>
      </div>
    </PublicLayout>
  );

  const { evento, locale } = data;
  const lang = i18n.language?.startsWith('en') ? 'en' : 'it';
  const titolo = (lang === 'en' && evento.titolo_en) ? evento.titolo_en : evento.titolo;
  const descr = (lang === 'en' && evento.descrizione_en) ? evento.descrizione_en : evento.descrizione;
  const showBuy = !!(evento.link_acquisto && evento.link_acquisto.trim());
  const showTable = !!evento.tavolo_prive && !!(evento.link_tavolo && evento.link_tavolo.trim());
  const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';
  const jsonLd = eventJsonLd(evento, locale, baseUrl);
  const dateFmt = new Date(evento.data + 'T00:00:00').toLocaleDateString(lang === 'en' ? 'en-GB' : 'it-IT', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  const handleBuy = () => { trackEvent('click_acquista', { event_id: evento.id }); trackEventBackend(evento.slug, 'buy'); window.open(evento.link_acquisto, '_blank', 'noopener'); };
  const handleTable = () => { trackEvent('click_tavolo', { event_id: evento.id }); trackEventBackend(evento.slug, 'table'); window.open(evento.link_tavolo, '_blank', 'noopener'); };

  const imageSrc = getEventImage(evento, locale);

  return (
    <PublicLayout>
      <SEO
        title={`${titolo} — ${locale?.nome || 'Roma'} • ${evento.zona}`}
        description={truncate(descr, 160) || `${titolo} il ${dateFmt} a ${locale?.nome || 'Roma'}, ${evento.zona}.`}
        canonical={`${baseUrl}/eventi/${evento.slug}`}
        image={evento.locandina}
        jsonLd={jsonLd}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[color:var(--text-muted)] hover:text-[color:var(--gold)] mb-4" data-testid="event-detail-back"><ArrowLeft className="w-4 h-4"/> {t('common.back')}</Link>
      </div>

      <article className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[1.1fr_1fr] gap-8 pb-32 lg:pb-12">
        <div>
          <div className="relative rounded-2xl overflow-hidden border border-[color:var(--border-c)] bg-[color:var(--surface)] aspect-[4/5] sm:aspect-[16/10] lg:aspect-[3/4]">
            {imageSrc ? <img src={imageSrc} alt={titolo} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[color:var(--text-subtle)]">No image</div>}
            <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
              {evento.in_evidenza && <Badge className="bg-[color:var(--gold-strong)] text-black">{t('event.featuredBadge')}</Badge>}
              {evento.tavolo_prive && <Badge variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)]"><Crown className="w-3 h-3 mr-1"/>{t('event.priveBadge')}</Badge>}
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold)]">{evento.zona} • Roma</p>
          <h1 data-testid="event-detail-title" className="font-heading text-3xl md:text-5xl mt-2 leading-tight">{titolo}</h1>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <InfoRow icon={Calendar} label={t('event.date')} value={dateFmt} />
            <InfoRow icon={Calendar} label={t('event.time')} value={evento.orario} />
            {locale && <InfoRow icon={Building2} label={t('event.venue')} value={<Link to={`/locali/${locale.slug}`} className="hover:text-[color:var(--gold)]">{locale.nome}</Link>} />}
            <InfoRow icon={MapPin} label={t('event.zone')} value={<Link to={`/serate-roma-${zoneSlug(evento.zona)}`} className="hover:text-[color:var(--gold)]">{evento.zona}</Link>} />
            {evento.artisti && <InfoRow icon={Music} label={t('event.artists')} value={evento.artisti} />}
            {evento.genere_musicale && <InfoRow icon={Music} label={t('event.genre')} value={evento.genere_musicale} />}
            {evento.prezzo_ingresso && <InfoRow icon={Euro} label={t('event.price')} value={evento.prezzo_ingresso} />}
          </div>

          {/* Desktop CTA */}
          <div className="mt-6 hidden md:flex gap-3">
            {showBuy && <Button onClick={handleBuy} data-testid="event-detail-buy-button" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold btn-press"><Ticket className="w-4 h-4 mr-2"/> {t('event.buy')}</Button>}
            {showTable && <Button onClick={handleTable} data-testid="event-detail-table-button" variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[rgba(214,185,140,0.08)] btn-press"><Crown className="w-4 h-4 mr-2"/> {t('event.table')}</Button>}
          </div>

          {descr && (
            <div className="mt-8">
              <h2 className="font-heading text-xl mb-2">{t('event.details')}</h2>
              <div className="prose prose-invert max-w-none text-[color:var(--text-muted)] leading-relaxed whitespace-pre-line">{descr}</div>
            </div>
          )}

          {locale && (
            <div className="mt-8 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)] p-5">
              <h2 className="font-heading text-xl mb-2">{t('event.location')}</h2>
              <p className="text-[color:var(--text)] font-medium">{locale.nome}</p>
              {locale.indirizzo && <p className="text-sm text-[color:var(--text-muted)]">{locale.indirizzo}</p>}
              <Link to={`/locali/${locale.slug}`} className="text-sm text-[color:var(--gold)] hover:text-[color:var(--gold-soft)] mt-3 inline-block">Vedi pagina locale →</Link>
            </div>
          )}
        </div>
      </article>

      {/* Mobile Sticky CTA */}
      {(showBuy || showTable) && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden border-t border-[color:var(--border-c)] bg-[color:var(--bg-2)] p-3 flex gap-2">
          {showBuy && <Button onClick={handleBuy} data-testid="event-detail-buy-button-mobile" className="bg-[color:var(--gold-strong)] hover:bg-[#C6A21A] text-black font-semibold flex-1 btn-press"><Ticket className="w-4 h-4 mr-2"/>{t('event.buy')}</Button>}
          {showTable && <Button onClick={handleTable} data-testid="event-detail-table-button-mobile" variant="outline" className="border-[color:var(--gold)] text-[color:var(--gold)] flex-1 btn-press"><Crown className="w-4 h-4 mr-2"/>{t('event.table')}</Button>}
        </div>
      )}
    </PublicLayout>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-[color:var(--border-c)] bg-[color:var(--surface)] p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[color:var(--text-subtle)]"><Icon className="w-3.5 h-3.5 text-[color:var(--gold)]"/>{label}</div>
      <div className="text-sm mt-1 text-[color:var(--text)]">{value}</div>
    </div>
  );
}
